import React from "react";
import { Truck, Globe2, MoveRight, MapPin, CreditCard, Clock, Satellite, Package, Users, RefreshCw, } from "lucide-react";
import { motion } from "framer-motion";
import { Helmet } from 'react-helmet-async';
import lttimg from "../assets/images/data/ltt2.png";
import MoreServicesSection from './MoreServicesSection'
// import FooterCTA from './FooterCTA'
import CTA from "./CTA";

// const features = [
//   {
//     icon: <CheckCircle className="w-6 h-6 text-yellow-300" />,
//     title: "Cost-Effective Shipping",
//   },
//   {
//     icon: <Timer className="w-6 h-6 text-yellow-300" />,
//     title: "Flexible Scheduling",
//   },
//   {
//     icon: <Globe2 className="w-6 h-6 text-yellow-300" />,
//     title: "Pan India Coverage",
//   },
//   {
//     icon: <ShieldCheck className="w-6 h-6 text-yellow-300" />,
//     title: "Secure Handling",
//   },
//   {
//     icon: <PackageSearch className="w-6 h-6 text-yellow-300" />,
//     title: "Real-Time Tracking",
//   },
//   {
//     icon: <Truck className="w-6 h-6 text-yellow-300" />,
//     title: "Fast Transit",
//   },
// ];

// const fadeUp = {
//   hidden: { opacity: 0, y: 30 },
//   visible: i => ({
//     opacity: 1,
//     y: 0,
//     transition: { delay: i * 0.1, duration: 0.6 }
//   })
// };

const PTLPage = () => (
  <main className="bg-white text-gray-800 overflow-hidden relative font-sans">
    <Helmet>
      <title>Part Truck Load (PTL) | S INFINITE EXPRESS</title>
      <meta
        name="description"
        content="S INFINITE EXPRESS delivers affordable and reliable Part Truck Load (PTL) shipping with real-time tracking, shared truck services, and pan-India logistics coverage."
      />
      <meta
        name="keywords"
        content="PTL, part truck load, partial load logistics, shared truck transport, part load shipping India, affordable PTL freight, India PTL transport service, S Infinite Express PTL, part load courier services, freight forwarding India, B2B logistics PTL, multi-location delivery, partial truck booking, logistics company India, LTL India, small shipment transport, nationwide PTL service, intercity freight movement"
      />
      <meta name="robots" content="index, follow" />
      <link rel="canonical" href="https://sinfiniteexpress.com/services/ptl" />

      <meta property="og:title" content="Part Truck Load (PTL) | S INFINITE EXPRESS" />
      <meta
        property="og:description"
        content="Reliable and cost-effective PTL logistics service by S INFINITE EXPRESS — perfect for shared trucking, partial loads, and nationwide delivery in India."
      />
      <meta property="og:url" content="https://sinfiniteexpress.com/services/ptl" />
      <meta property="og:type" content="website" />
    </Helmet>

    {/* INTRO */}
    <section className="max-w-7xl mx-auto py-20 px-6 md:px-12 grid md:grid-cols-2 gap-12 items-center">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="space-y-6"
      >
        <h2 className="text-3xl md:text-4xl font-bold text-[#211f57]">Why Choose Our PTL?</h2>
        <div className="w-20 h-1 bg-[#d32c30] rounded"></div>
        <p className="text-gray-700 text-lg">
          <span className="font-bold text-[#d32c30]">S INFINITE EXPRESS</span> delivers dependable
          <span className="text-[#211f57] font-semibold"> Partial Truckload (PTL)</span> services designed for businesses with moderate shipping volumes.
          With 300+ GPS-enabled trucks and a reach across
          <span className="text-[#211f57] font-semibold"> 18,000+ pin codes</span>, we ensure fast, secure, and cost-effective transportation across India.
        </p>

        <div className="bg-gradient-to-r from-[#fff44f] via-[#fff86f] to-[#fffcb0] text-black p-6 rounded-xl shadow-lg space-y-3">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            🚀 Nationwide, On Your Terms
          </h3>
          <p className="text-black">
            Reliable, cost-effective PTL services across India — built for your business.
          </p>
          <ul className="list-disc list-inside text-black text-sm space-y-1">
            <li>Door-to-door and hub-to-hub solutions</li>
            <li>Flexible scheduling with reduced freight cost</li>
            <li>Ideal for small to medium shipments</li>
            <li>Digital tracking & centralized customer support</li>
          </ul>
          <a
            href="/get-a-quote"
            className="inline-flex items-center gap-2 text-white px-4 py-2 bg-[#d32c30] rounded-md text-sm font-medium hover:bg-[#b82629] transition"
          >
            Get Started <MoveRight className="w-4 h-4" />
          </a>
        </div>
      </motion.div>

      <motion.img
        src={lttimg}
        alt="PTL Fleet"
        className="rounded-3xl shadow-2xl hover:scale-105 transition-transform duration-700 border-4 border-white"
        loading="lazy"
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      />
    </section>

    {/* Features */}
    <div className="py-20 bg-gradient-to-br from-[#211f57] to-[#2f2c72]">
      <div className="max-w-7xl mx-auto px-4">
        <h2 className="text-3xl md:text-4xl font-extrabold text-center text-[#fff44f] mb-4">
          PTL Services: What We Deliver on Every Route
        </h2>
        <p className="text-center text-white max-w-3xl mx-auto mb-12">
          Explore the strengths that power our Part Truck Load services—where technology, scale, and dedication come together to move your business forward.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {[
            {
              icon: <Truck className="w-6 h-6 text-blue-600" />,
              bg: "bg-blue-100",
              title: '300+ All-Weather Vehicles',
              description: 'Fully containerized trucks and trailers to keep every shipment safe in any condition.',
            },
            {
              icon: <MapPin className="w-6 h-6 text-green-600" />,
              bg: "bg-blue-50",
              title: '1 Million+ Kilometers Daily',
              description: 'Covering metro cities and remote destinations with unmatched reach.',
            },
            {
              icon: <CreditCard className="w-6 h-6 text-purple-600" />,
              bg: "bg-blue-100",
              title: 'Flexible Billing Options',
              description: 'Paid, To-Pay, and Prepaid freight solutions customized to your operations.',
            },
            {
              icon: <Package className="w-6 h-6 text-yellow-600" />,
              bg: "bg-blue-50",
              title: 'Door-to-Door Delivery',
              description: 'End-to-end logistics—from your warehouse to your customer’s doorstep.',
            },
            {
              icon: <Satellite className="w-6 h-6 text-orange-600" />,
              bg: "bg-blue-100",
              title: '24/7 GPS Tracking',
              description: 'Real-time monitoring and instant updates on every consignment.',
            },
            {
              icon: <Clock className="w-6 h-6 text-red-600" />,
              bg: "bg-blue-50",
              title: 'Priority Transit & Express Lanes',
              description: 'Accelerated delivery schedules to meet your most critical deadlines.',
            },
            {
              icon: <RefreshCw className="w-6 h-6 text-teal-600" />,
              bg: "bg-blue-100",
              title: 'Reverse Logistics Simplified',
              description: 'Streamlined returns and reverse supply chain management.',
            },
            {
              icon: <Users className="w-6 h-6 text-pink-600" />,
              bg: "bg-blue-50",
              title: 'Expert Logistics Team',
              description: '5,500+ professionals dedicated to accuracy and care.',
            },
            {
              icon: <Globe2 className="w-6 h-6 text-[#211f57]" />,
              bg: "bg-blue-100",
              title: 'Pan-India Coverage',
              description: 'Serving 18,000+ pin codes with reliability and precision.',
            },
          ].map((feature, index) => (
            <div
              key={index}
              className={`group ${feature.bg} p-6 rounded-xl backdrop-blur-sm shadow-md hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300 hover:bg-gradient-to-r from-[#fff44f] via-[#fff86f] to-[#fffcb0]`}
            >
              <div className="flex items-center justify-center mb-4">
                <div className="w-12 h-12 rounded-full border-2 border-gray-300 flex items-center justify-center transition-colors duration-300 group-hover:bg-white">
                  <div className="transition-colors duration-300 group-hover:text-yellow-600">
                    {feature.icon}
                  </div>
                </div>
              </div>

              <h3 className="text-lg font-bold text-[#211f57] group-hover:text-[#211f57] mb-2 group relative inline-block">
                {feature.title}
                <span className="absolute left-0 -bottom-1 h-0.5 w-0 bg-[#211f57] transition-all duration-300 group-hover:w-full"></span>
              </h3>

              <p className="text-sm text-gray-700 group-hover:text-[#211f57] transition-colors duration-300">
                {feature.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>

    <MoreServicesSection />
    <CTA />
  </main>
);

export default PTLPage;
